import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Building, Checkmark, ChevronDown } from "@carbon/icons-react";
import { useUser } from "../../context";
import { CompanyResponse } from "../../types/company.type";

const CompanySwitcher = () => {
  const { user, companyList, changeCompany, isLoading } = useUser();

  if (!companyList || companyList.length < 2) return null;

  const currentId = user?.company?.id;
  const currentName =
    companyList.find((c) => c.id === currentId)?.name ??
    user?.company?.name ??
    "";

  const handleSelect = async (companyId: string) => {
    if (!companyId || companyId === currentId) return;
    await changeCompany(companyId);
  };

  return (
    <Menu as="div" className="relative ml-3">
      <Menu.Button
        className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-spectum-light disabled:opacity-60"
        disabled={isLoading}
      >
        <Building size={16} className="text-spectum" />
        <span className="text-sm font-medium text-gray-900 max-w-[12rem] truncate">
          {currentName}
        </span>
        <ChevronDown size={16} className="text-gray-400" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="origin-top-right absolute right-0 mt-2 w-64 max-h-80 overflow-y-auto rounded shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-30">
          {companyList.map((company: CompanyResponse) => {
            const isCurrent = company.id === currentId;
            return (
              <Menu.Item key={company.id} disabled={isCurrent}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => handleSelect(company.id)}
                    className={`${
                      active && !isCurrent ? "bg-gray-100" : ""
                    } flex items-center justify-between w-full px-4 py-2 text-sm text-left ${
                      isCurrent
                        ? "text-spectum font-semibold cursor-default"
                        : "text-gray-700"
                    }`}
                  >
                    <span className="truncate">{company.name}</span>
                    {isCurrent && (
                      <Checkmark size={16} className="text-spectum shrink-0 ml-2" />
                    )}
                  </button>
                )}
              </Menu.Item>
            );
          })}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default CompanySwitcher;
